import { Alert, Button, Card, Descriptions, Form, Input, Space, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import LmsStatusTag from '@/pages/education/components/LmsStatusTag';
import { lmsErrorMessage, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type MyHomework = {
  assignment: { id: string; title: string; instructions?: string | null; due_on: string; status: string; subject_name?: string; section_name?: string };
  student_id: string;
  submission: {
    id: string; status: string; submitted_at?: string; body_text?: string | null; evidence_url?: string | null;
    feedback_text?: string | null; feedback_at?: string | null;
  } | null;
};
type Values = { body_text?: string; evidence_url?: string };

/** Student (or granted guardian via ?student_id=) submission page. No payment actions. */
export default function HomeworkSubmitPage() {
  const { t } = useTranslation('education');
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const studentId = params.get('student_id') || undefined;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canSubmit } = useLmsAccess();
  const mine = useQuery({
    queryKey: ['education-lms-my-homework', id, studentId],
    enabled: canSubmit,
    queryFn: async () => (await api.get<MyHomework>(`/education/lms/my/homework/${id}`, { params: { student_id: studentId } })).data,
  });
  const submit = useMutation({
    mutationFn: (values: Values) => api.post(`/education/lms/homework/${id}/submit`, {
      student_id: studentId,
      body_text: values.body_text?.trim() || null,
      evidence_url: values.evidence_url?.trim() || null,
    }),
    onSuccess: () => {
      message.success(t('lmsSubmittedOk', { defaultValue: 'Homework submitted' }));
      queryClient.invalidateQueries({ queryKey: ['education-lms-my-homework', id] });
      queryClient.invalidateQueries({ queryKey: ['education-portal-student'] });
    },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to submit homework')),
  });
  const a = mine.data?.assignment;
  const submission = mine.data?.submission;

  return (
    <EducationPageChrome
      title={a?.title || t('lmsSubmitHomework', { defaultValue: 'Submit homework' })}
      description={a ? [a.section_name, a.subject_name].filter(Boolean).join(' · ') : undefined}
      tags={submission ? <LmsStatusTag status={submission.status} /> : undefined}
      extra={<Button onClick={() => navigate(-1)}>{t('back', { defaultValue: 'Back' })}</Button>}
    >
      {!canSubmit ? <Alert type="warning" showIcon message={t('lmsCannotSubmit', { defaultValue: 'You do not have permission to submit homework.' })} /> : null}
      {mine.isError ? <Alert type="error" showIcon message={lmsErrorMessage(mine.error, 'Unable to load homework')} /> : null}
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 820 }}>
        <Card size="small" loading={mine.isLoading}>
          {a ? (
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('lmsDueOn', { defaultValue: 'Due on' })}>{a.due_on}</Descriptions.Item>
              <Descriptions.Item label={t('lmsInstructions', { defaultValue: 'Instructions' })}>
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{a.instructions || '—'}</Typography.Paragraph>
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>
        {submission ? (
          <Card size="small" title={t('lmsSubmitted', { defaultValue: 'Submitted' })}>
            <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('lmsAlreadySubmitted', { defaultValue: 'This homework was already submitted.' })} />
            {submission.body_text ? <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{submission.body_text}</Typography.Paragraph> : null}
            {submission.evidence_url ? <a href={submission.evidence_url} target="_blank" rel="noreferrer">{submission.evidence_url}</a> : null}
            {submission.feedback_text ? (
              <Alert style={{ marginTop: 12 }} type="success" showIcon message={t('lmsFeedback', { defaultValue: 'Feedback' })} description={submission.feedback_text} />
            ) : null}
          </Card>
        ) : a && a.status === 'published' ? (
          <Card size="small" title={t('lmsSubmitHomework', { defaultValue: 'Submit homework' })}>
            <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('lmsSubmitHint', { defaultValue: 'Submit text and/or a link. One submission is accepted per assignment.' })} />
            <Form<Values> layout="vertical" disabled={!canSubmit} onFinish={(v) => submit.mutate(v)}>
              <Form.Item name="body_text" label={t('lmsAnswer', { defaultValue: 'Your answer' })}>
                <Input.TextArea rows={8} maxLength={20000} showCount />
              </Form.Item>
              <Form.Item name="evidence_url" label={t('lmsEvidenceUrl', { defaultValue: 'Link to your work (optional)' })} rules={[{ type: 'url', message: 'Enter a valid URL' }]}>
                <Input maxLength={1000} placeholder="https://…" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submit.isPending}>
                {t('lmsSubmitHomework', { defaultValue: 'Submit homework' })}
              </Button>
            </Form>
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
